import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Search, RefreshCw, Send, CheckCircle, Clock } from 'lucide-react';
import { config } from '../../config';

const AdminChatConsole = ({ currentUser }) => {
    const [threads, setThreads] = useState({});
    const [activeThreadId, setActiveThreadId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [lastSync, setLastSync] = useState(new Date(0).toISOString());

    const messagesEndRef = useRef(null);

    // Initial Load & Polling
    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 900000); // Poll every 15m
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [activeThreadId, threads]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    const fetchMessages = async () => {
        setIsLoading(true);
        try {
            // We ask for ALL messages since lastSync. 
            // NOTE: The backend 'poll_messages' currently filters by email.
            // We might need to send a specific "role=ADMIN" flag if the backend restricts it.
            // For now, let's assume the Admin Email is a participant in all 'COACH' threads 
            // OR we pass a 'superuser' flag if we updated the workflow (we haven't yet, so we rely on email).

            // WORKAROUND: If the Orchestrator restricts by Email, the Admin needs to be the recipient 'COACH' or explicitly included.
            // Let's try fetching and see. If it returns empty, I'll need to update the Orchestrator to allow Admins to see all.

            const url = `${config.API_BASE_URL}/webhook/chat/poll?email=${currentUser.email}&role=${currentUser.role}&lastSync=${encodeURIComponent(lastSync)}&action=poll_messages`;
            const res = await fetch(url);
            if (res.ok) {
                const newMsgs = await res.json();
                if (newMsgs && newMsgs.length > 0) {
                    processMessages(newMsgs);
                    // Update lastSync to the latest timestamp found
                    const latest = newMsgs.reduce((max, msg) => new Date(msg.Timestamp) > new Date(max) ? msg.Timestamp : max, lastSync);
                    setLastSync(latest);
                }
            }
        } catch (e) {
            console.error("Admin Chat Poll Failed", e);
        } finally {
            setIsLoading(false);
        }
    };

    const processMessages = (messages) => {
        setThreads(prev => {
            const next = { ...prev };
            messages.forEach(msg => {
                const threadId = msg.ThreadID || `unknown-${msg.Sender_Email}`; // Fallback if threadId missing

                // If it's a new thread, init it
                if (!next[threadId]) {
                    // Try to infer Client Name/Email from the message if possible
                    // Usually the 'Sender' is the client if direction is User->Coach
                    const clientEmail = msg.Sender_Email === currentUser.email ? msg.Recipient_Email : msg.Sender_Email;

                    next[threadId] = {
                        id: threadId,
                        clientName: clientEmail, // Placeholder until we have a proper name lookup
                        clientEmail: clientEmail,
                        messages: [],
                        lastActivity: msg.Timestamp,
                        unreadCount: 0
                    };
                }

                // Avoid Duplicates
                if (!next[threadId].messages.find(m => m.MessageID === msg.MessageID)) {
                    next[threadId].messages.push(msg);
                    next[threadId].lastActivity = msg.Timestamp;

                    if (msg.Sender_Email !== currentUser.email && msg.Read_Status !== 'read') {
                        next[threadId].unreadCount += 1;
                    }
                }
            });

            // Sort messages in each thread
            Object.values(next).forEach(t => {
                t.messages.sort((a, b) => new Date(a.Timestamp) - new Date(b.Timestamp));
            });

            return next;
        });
    };

    const sendReply = async (e) => {
        e.preventDefault();
        if (!replyText.trim() || !activeThreadId) {return;}

        const thread = threads[activeThreadId];
        const draft = {
            action: 'send_message',
            threadId: thread.id,
            sender: currentUser.email,
            recipient: thread.clientEmail, // Reply to the client
            content: replyText,
            context: { role: 'ADMIN_CONSOLE' },
            timestamp: new Date().toISOString(),
            readStatus: 'unread'
        };

        // Optimistic Update
        const newMsg = {
            MessageID: `admin-${Date.now()}`,
            ThreadID: thread.id,
            Sender_Email: currentUser.email,
            Recipient_Email: thread.clientEmail,
            Content: replyText,
            Timestamp: new Date().toISOString()
        };

        setThreads(prev => ({
            ...prev,
            [activeThreadId]: {
                ...prev[activeThreadId],
                messages: [...prev[activeThreadId].messages, newMsg]
            }
        }));

        setReplyText('');

        try {
            await fetch(`${config.API_BASE_URL}/webhook/chat/send`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(draft)
            });
        } catch (e) {
            console.error("Send Failed", e);
            alert("Failed to send message");
        }
    };

    const sortedThreads = Object.values(threads).sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));

    return (
        <div className="flex h-full bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shadow-sm">

            {/* Thread List (Left) */}
            <div className="w-1/3 border-r border-slate-200 bg-white flex flex-col">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h3 className="font-bold text-slate-700">Inbox</h3>
                    <button onClick={fetchMessages} className={`p-2 rounded-full hover:bg-slate-200 text-slate-500 ${isLoading ? 'animate-spin' : ''}`}>
                        <RefreshCw size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {sortedThreads.length === 0 ? (
                        <div className="p-8 text-center text-slate-400 text-sm">No conversations yet.</div>
                    ) : (
                        sortedThreads.map(thread => (
                            <button
                                key={thread.id}
                                onClick={() => setActiveThreadId(thread.id)}
                                className={`w-full text-left p-4 border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeThreadId === thread.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''}`}
                            >
                                <div className="flex justify-between mb-1">
                                    <span className="font-bold text-slate-800 text-sm truncate">{thread.clientEmail}</span>
                                    <span className="text-[10px] text-slate-400">{new Date(thread.lastActivity).toLocaleDateString()}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <p className="text-xs text-slate-500 truncate w-2/3">
                                        {thread.messages[thread.messages.length - 1]?.Content || 'No messages'}
                                    </p>
                                    {thread.unreadCount > 0 && (
                                        <span className="bg-blue-600 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">
                                            {thread.unreadCount}
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))
                    )}
                </div>
            </div>

            {/* Message View (Right) */}
            <div className="flex-1 flex flex-col bg-slate-50/50">
                {activeThreadId ? (
                    <>
                        {/* Header */}
                        <div className="p-4 bg-white border-b border-slate-200 flex justify-between items-center shadow-sm z-10">
                            <div>
                                <h3 className="font-bold text-slate-800">{threads[activeThreadId].clientEmail}</h3>
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span className="flex items-center gap-1"><Clock size={12} /> Last Active: {new Date(threads[activeThreadId].lastActivity).toLocaleString()}</span>
                                </div>
                            </div>
                            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                Thread ID: {activeThreadId.substring(0, 8)}
                            </div>
                        </div>

                        {/* Messages */}
                        <div className="flex-1 p-6 overflow-y-auto space-y-4">
                            {threads[activeThreadId].messages.map((msg, i) => {
                                const isMe = msg.Sender_Email === currentUser.email;
                                return (
                                    <div key={i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[70%] p-4 rounded-2xl shadow-sm ${isMe
                                            ? 'bg-blue-600 text-white rounded-tr-none'
                                            : 'bg-white text-slate-800 border border-slate-200 rounded-tl-none'
                                            }`}>
                                            <p className="text-sm">{msg.Content}</p>

                                            {/* Context Info if available */}
                                            {msg.Context_Data && msg.Context_Data !== '{}' && !isMe && (
                                                <div className="mt-2 pt-2 border-t border-slate-100/20 text-[10px] opacity-70">
                                                    Context: {msg.Context_Data}
                                                </div>
                                            )}

                                            <div className="mt-1 text-[10px] opacity-60 text-right">
                                                {new Date(msg.Timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input */}
                        <div className="p-4 bg-white border-t border-slate-200">
                            <form onSubmit={sendReply} className="flex gap-2">
                                <input
                                    type="text"
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type your reply..."
                                    className="flex-1 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                />
                                <button
                                    type="submit"
                                    disabled={!replyText.trim()}
                                    className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-lg shadow-blue-500/20"
                                >
                                    <Send size={20} />
                                </button>
                            </form>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                        <MessageCircle size={48} className="mb-4 opacity-50" />
                        <p className="font-medium">Select a conversation to start chatting</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminChatConsole;
